import { Box, BoxProps, MenuButton } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { useLikeActivityMutation } from "src/api/client/like.api";
import { IUnderlyingsInTextData, ITopTradersReactions } from "src/api/server/news.types";
import { Button } from "src/components/base/button";
import { LikeButton } from "src/components/common/buttons/like-button/like-button";
import { EnumLikeableEntityType } from "src/components/common/buttons/like-button/like-button.types";
import { ShareButton } from "src/components/common/buttons/share/share-button";
import { useShareLinksBasic } from "src/components/common/buttons/share/share-button-util";
import { IconShare } from "src/components/icons";
import { useNavHeight } from "src/components/layout/hooks/use-nav-height";
import { ILikeInfo } from "src/types/common";
import { isServerSide } from "src/utils/common-util";
import { useRouterCaller } from "src/utils/router/use-router-caller";
import { ParagraphsWithInjectedContent } from "./components/paragraphs-with-injected-content";
import { FeaturedTopTraderReactionCard } from "./components/trader-reaction-card/trader-reaction-card";
import { NEWS_ARTICLE_SECTION_TEST_ID } from "./news.test-ids";

interface IArticleSectionProps extends BoxProps {
    paragraphs: string[];
    likeInfo: ILikeInfo;
    headline: string;
    likeSectionId: string;
    topTradersReactions: ITopTradersReactions | null;
    underlyingsInTextData: IUnderlyingsInTextData[];
}

export const ArticleSection = ({
    paragraphs = [],
    likeInfo,
    headline,
    topTradersReactions,
    likeSectionId,
    underlyingsInTextData,
    ...boxProps
}: IArticleSectionProps) => {
    const { t } = useTranslation("common");
    const { NAV_HEIGHT_VARIATION } = useNavHeight();
    const { isCallerContentApp } = useRouterCaller();
    const slug = useRouter().query.slug as string;
    const url = isServerSide() ? "" : window.location.href;
    const shareOptions = useShareLinksBasic(headline, "", url);
    const likeActivity = useLikeActivityMutation();

    return (
        <Box {...boxProps} fontSize={["md", "md", "lg"]} data-test-id={NEWS_ARTICLE_SECTION_TEST_ID}>
            <ParagraphsWithInjectedContent
                underlyingsInTextData={underlyingsInTextData}
                paragraphs={paragraphs}
                ComponentToInject={
                    topTradersReactions !== null && topTradersReactions?.topTraderReaction ? (
                        <FeaturedTopTraderReactionCard topTraderReaction={topTradersReactions.topTraderReaction} mb={[2, 4]} />
                    ) : null
                }
            />

            <LikeButton
                scrollMarginTop={isCallerContentApp ? 0 : NAV_HEIGHT_VARIATION.map(m => m + 1)}
                id={likeSectionId}
                size="sm"
                likeInfo={likeInfo}
                entityReferences={[{ entityId: slug, entityType: EnumLikeableEntityType.ExternalNewsArticle }]}
                label={t("like")}
                onClick={() => likeActivity({ activityType: EnumActivityType.NewsArticleLiked })}
            />
            <ShareButton link={url} shareOptions={shareOptions} webShareData={{ url, title: headline }}>
                <MenuButton as={Button} size="sm" variant="ghost" px={2} leftIcon={<IconShare boxSize={3} />}>
                    {t("share")}
                </MenuButton>
            </ShareButton>
        </Box>
    );
};
